import { useEffect, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Panel, PanelHeader } from "./Panel";

/**
 * Modal — boîte de dialogue centrée (confirmations d'action admin, formulaires).
 * Ferme sur Échap et clic sur le fond. Contrôlé par `open` + `onClose`.
 */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZES = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" };

export function Modal({ open, onClose, title, subtitle, children, footer, size = "md", className }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center p-2 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/85"
        onClick={onClose}
        aria-hidden
      />
      <Panel
        rivets
        glow="none"
        className={cn("relative z-10 w-full max-h-[calc(100dvh-1rem)] animate-menu-in overflow-hidden", SIZES[size], className)}
      >
        {title && <PanelHeader title={title} {...(subtitle ? { subtitle } : {})} />}
        <div className="touch-scroll-y max-h-[calc(100dvh-12rem)] overflow-y-auto p-4 sm:p-5">{children}</div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-bronze-600/30 px-4 py-3 [&>*]:w-full sm:flex-row sm:justify-end sm:px-5 sm:py-4 sm:[&>*]:w-auto">{footer}</div>
        )}
      </Panel>
    </div>
  );
}
